import type { ManagedCall } from '@repo/sdk/gen'
import {
	Alert,
	AlertAction,
	AlertDescription,
	AlertTitle
} from '@repo/ui/alert'
import { Button } from '@repo/ui/button'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@repo/ui/tabs'
import { cn } from '@repo/ui/utils'
import { useQueryClient, useSuspenseQuery } from '@tanstack/react-query'
import { createFileRoute, Link } from '@tanstack/react-router'
import { CheckCircle2, Info } from 'lucide-react'
import { toast } from 'sonner'
import { z } from 'zod'
import { type TKey, useTranslation } from '@/lib/i18n'
import { makeQueryKeys } from '@/query/query-keys'
import { CallStateBadge } from '../../-components/call-state-badge'
import { ResidenceIssues } from '../../-components/residence-issues'
import { ResidencePhotos } from '../../-components/residence-photos'
import { openSteps, unmetConditions } from '../../bando/-lib/steps'
import { managedCallQueryOptions } from '../../bando/-query/query-options'
import {
	ContractFields,
	GroupErrorDot,
	IdentityFields,
	ResidenceForm,
	SeatsFields,
	ServicesFields
} from '../../residences/-components/residence-form'
import { SitesFields } from '../../residences/-components/sites-fields'
import {
	dataOf,
	EMPTY_SITE,
	type ResidenceFormData,
	saveResidence
} from '../../residences/-lib/residence-form'
import { residenceDetailQueryOptions } from '../../residences/-query/query-options'
import {
	contractMissing,
	hasFields,
	type StructureTab,
	TAB_GROUP,
	TABS
} from '../-lib/structure-tabs'
import { Quota70Tab } from './-components/quota70-tab'

export const Route = createFileRoute('/(private)/strutture/$residenceId/')({
	validateSearch: z.object({
		tab: z.enum(TABS).optional().catch(undefined),
		/** The page of the 70% list in its tab. */
		page: z.number().int().min(1).optional().catch(undefined)
	}),
	component: StructurePage
})

/**
 * One residence of the facility manager: the data its call is written from,
 * and its photos. The general admin creates the residence, and the manager
 * completes and corrects every field until the call is published (decision
 * D73). Data still incomplete saves: it only stops the publication, and the
 * page lists it. The data stays editable once the call is published: it is what
 * the next call is made from, and the published call keeps its own copy, with
 * the two corrections it still takes on its page. A change voids the data
 * confirmation of a draft, which the manager gives again before publishing.
 */
function StructurePage() {
	const { t } = useTranslation()
	const queryClient = useQueryClient()
	const { residenceId } = Route.useParams()
	const navigate = Route.useNavigate()
	const myCall = useSuspenseQuery(managedCallQueryOptions(residenceId)).data
	// What the form edits is the residence as it is now, which the next call is
	// made from: a published call shows the copy it was published with.
	const live = useSuspenseQuery(residenceDetailQueryOptions(residenceId)).data
	const residence = live ?? myCall.residence
	const frozen = myCall.state !== 'draft'
	// The 70% quota has applications only once the call opened.
	const tabs = TABS.filter(
		(value) =>
			value !== 'quota70' ||
			myCall.state === 'open' ||
			myCall.state === 'closed'
	)
	const asked = Route.useSearch().tab ?? 'identity'
	const tab = tabs.includes(asked) ? asked : 'identity'
	// The photos save on their own: no save bar under them.
	const noSaveBar = tab === 'photos'
	const saved = dataOf(residence)
	const save = async (data: ResidenceFormData) => {
		if (!(await saveResidence(residence, data))) {
			toast.info(t('admin-residences:detail.no_changes'))
			return
		}
		// Name, seats and next step also show on the lists.
		await Promise.all([
			queryClient.invalidateQueries({
				queryKey: makeQueryKeys.call.all()
			}),
			queryClient.invalidateQueries({
				queryKey: makeQueryKeys.residences.all()
			})
		])
		toast.success(
			frozen
				? t('admin-call:residence.saved_next_call')
				: t('admin-call:residence.saved')
		)
	}
	// The first step asks the address of the residence: a residence without a
	// site yet starts with one, named as the residence.
	const initial =
		saved.sites.length === 0 && !frozen
			? { ...saved, sites: [{ ...EMPTY_SITE, name: saved.name }] }
			: saved

	return (
		<main
			className={cn(
				'flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto px-4 pt-4 md:px-6 md:pt-6',
				// The save bar pins to the bottom edge and takes the padding there.
				noSaveBar && 'pb-4 md:pb-6'
			)}
		>
			<header className='flex flex-wrap items-center gap-x-4 gap-y-1'>
				<h1 className='font-semibold text-xl'>{residence.name}</h1>
				{frozen ? (
					<>
						<CallStateBadge state={myCall.state} />
						<CallLink
							className='ml-auto'
							residenceId={residence.id}
						/>
					</>
				) : (
					<p className='text-muted-foreground text-sm'>
						{t('admin-call:residence.page_intro')}{' '}
						<span aria-hidden className='text-destructive'>
							*
						</span>{' '}
						{t('admin-residences:form.required_legend')}
					</p>
				)}
			</header>
			{frozen && (
				<Alert variant='info'>
					<Info aria-hidden />
					<AlertDescription>
						{t('admin-call:residence.next_call_note', {
							year: myCall.academicYear
						})}
					</AlertDescription>
				</Alert>
			)}
			<ResidenceIssues
				issues={residence.issues}
				residenceId={residence.id}
			/>
			{!frozen && <ResidenceReady myCall={myCall} />}
			<ResidenceForm
				defaultValues={initial}
				hideActions={tab === 'photos'}
				key={residence.updatedAt}
				onSubmit={save}
				submitLabel={t('admin-residences:detail.save')}
				tabs={Object.values(TAB_GROUP)}
			>
				<Tabs
					onValueChange={(value: StructureTab) =>
						navigate({
							search: (prev) => ({ ...prev, tab: value }),
							replace: true
						})
					}
					value={tab}
				>
					<div className='-mx-4 max-w-full overflow-x-auto overflow-y-hidden border-border border-b px-4 md:mx-0 md:px-0'>
						<TabsList variant='line'>
							{tabs.map((value) => (
								<TabsTrigger key={value} value={value}>
									{t(TAB_LABEL[value])}
									{hasFields(value) && (
										<GroupErrorDot
											group={TAB_GROUP[value]}
										/>
									)}
									{value === 'contract' &&
										contractMissing(saved) && (
											<span className='size-1.5 rounded-full bg-chart-1'>
												<span className='sr-only'>
													{t(
														'admin-residences:form.tab_to_fill'
													)}
												</span>
											</span>
										)}
								</TabsTrigger>
							))}
						</TabsList>
					</div>
					{tabs.map((value) => (
						<TabsContent
							className='flex flex-col gap-8 pt-4'
							keepMounted
							key={value}
							value={value}
						>
							{value === 'identity' && <IdentityFields />}
							{value === 'sites' && <SitesFields />}
							{value === 'services' && <ServicesFields />}
							{value === 'photos' && (
								<ResidencePhotos residence={residence} />
							)}
							{value === 'seats' && (
								<SeatsFields
									dsuRate={{
										single: residence.dsuSingleRateCents,
										double: residence.dsuDoubleRateCents
									}}
								/>
							)}
							{/* Only while open: the lists name the students, and each reading is logged as an access (D37). */}
							{value === 'quota70' && tab === 'quota70' && (
								<Quota70Tab myCall={myCall} />
							)}
							{value === 'contract' && <ContractFields />}
						</TabsContent>
					))}
				</Tabs>
			</ResidenceForm>
		</main>
	)
}

/**
 * The counterpart of the issues above: once the saved data holds everything the
 * call is written from, a green notice says so and points to the call, which
 * is ready to publish if no other step is open. The confirmation of the data
 * is the manager's act on the call page, so it does not count as missing here.
 */
function ResidenceReady({ myCall }: { myCall: ManagedCall }) {
	const { t } = useTranslation()
	const dataComplete =
		myCall.residence.issues.length === 0 &&
		unmetConditions(myCall, 'data').every(
			(check) => check.condition === 'dataConfirmed'
		)
	if (!dataComplete) {
		return null
	}
	const left = openSteps(myCall).length
	return (
		<Alert variant='success'>
			<CheckCircle2 aria-hidden />
			<AlertTitle>{t('admin-call:residence.data_complete')}</AlertTitle>
			<AlertDescription>
				{left === 0
					? t('admin-call:status.ready')
					: t('admin-call:status.draft', { count: left })}
			</AlertDescription>
			<AlertAction>
				<CallLink residenceId={myCall.residence.id} />
			</AlertAction>
		</Alert>
	)
}

function CallLink({
	residenceId,
	className
}: {
	residenceId: string
	className?: string
}) {
	const { t } = useTranslation()
	return (
		<Button
			className={className}
			nativeButton={false}
			render={<Link params={{ residenceId }} to='/bando/$residenceId' />}
			size='sm'
			variant='outline'
		>
			{t('admin-call:residence.go_to_call')}
		</Button>
	)
}

const TAB_LABEL = {
	identity: 'admin-residences:tabs.identity',
	sites: 'admin-residences:tabs.sites',
	services: 'admin-residences:tabs.services',
	photos: 'admin-residences:tabs.photos',
	seats: 'admin-residences:tabs.seats',
	contract: 'admin-residences:tabs.contract',
	quota70: 'admin-call:residence.tabs.quota70'
} as const satisfies Record<StructureTab, TKey>
